import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
} from '@stencil/core'

import Helpers from '../../../utils/helpers'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/skip-link?tab=designer).
 *
 * @slot default - Slot para o texto do link de navegação rápida.
 * @slot tag - Slot para personalizar a tag numérica do atalho de teclado, exibindo o valor de key-number por padrão.
 */
@Component({
  tag: 'br-skiplink-item',
  styleUrl: 'skip-link-item.scss',
  shadow: true,
})
export class SkipLinkItem {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrSkiplinkItemElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * ID do elemento de destino ou seletor CSS.
   * Este é o elemento para o qual o usuário será levado ao ativar este item.
   */
  @Prop({ reflect: true }) readonly target!: string

  /**
   * Número da tecla para acesso rápido.
   * Permite que o usuário ative este item pressionando Alt+[keyNumber].
   * Aceita valores de 1 a 9.
   * Esta propriedade implementa uma alternativa ao accessKey nativa,
   * fornecendo um atalho de teclado consistente e acessível.
   */
  @Prop({ reflect: true }) readonly keyNumber?: number

  /**
   * ID personalizado.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * Controla a visibilidade da tag numérica.
   * Quando `true`, a tag numérica não será exibida, mesmo que `keyNumber` esteja definido.
   * O atalho de teclado continua funcionando, apenas a indicação visual é ocultada.
   */
  @Prop({ reflect: true }) readonly hideTag: boolean = false

  /**
   * Estado interno que armazena o índice do item (1, 2, 3...).
   * Definido automaticamente pelo componente pai.
   */
  @State() private itemIndex: number | null = null

  /**
   * Estado interno que armazena o total de itens.
   * Definido automaticamente pelo componente pai.
   */
  @State() private itemTotal: number | null = null

  /**
   * MutationObserver para monitorar mudanças nos atributos de contagem.
   * Observa quando o componente pai define data-item-index e data-item-total.
   */
  private mutationObserver: MutationObserver | null = null

  /**
   * Evento emitido quando o item é clicado/ativado.
   * O evento contém informações sobre o item e o target.
   */
  @Event() brSkiplinkItemClick: EventEmitter<{ itemId: string; target: string }>

  /**
   * Evento emitido quando o item recebe foco.
   */
  @Event() brSkiplinkItemFocus: EventEmitter<{ itemId: string }>

  /**
   * Move o foco para este item.
   */
  @Method()
  async itemFocus(): Promise<void> {
    const brItemElement = this.el.shadowRoot?.querySelector('br-item') as HTMLElement | null

    if (brItemElement !== null) {
      await new Promise((resolve) => requestAnimationFrame(resolve))

      const brItemShadowRoot = brItemElement.shadowRoot
      if (brItemShadowRoot !== null) {
        const focusableElement = brItemShadowRoot.querySelector('a, button') as HTMLElement | null
        if (focusableElement !== null) {
          focusableElement.focus()
          this.brSkiplinkItemFocus.emit({ itemId: this.customId })
          return
        }
      }

      brItemElement.focus()
      this.brSkiplinkItemFocus.emit({ itemId: this.customId })
    }
  }

  /**
   * Navega para o target definido.
   * Move o foco para o elemento alvo e ajusta a rolagem da página se necessário.
   */
  @Method()
  async navigateToTarget(): Promise<void> {
    try {
      const targetElement = document.querySelector(this.target) as HTMLElement | null

      if (targetElement !== null) {
        targetElement.setAttribute('tabindex', '-1')
        targetElement.focus()

        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })

        this.brSkiplinkItemClick.emit({
          itemId: this.customId,
          target: this.target,
        })
      } else {
        console.warn(`Skip Link: Target element "${this.target}" not found`)
      }
    } catch (error) {
      console.error('Skip Link: Error navigating to target', error)
    }
  }

  /**
   * Listener para cliques no item.
   */
  @Listen('click')
  handleClick(_event: MouseEvent) {
    this.navigateToTarget()
  }

  /**
   * Listener para teclas Enter, Space e atalho Alt+[keyNumber].
   */
  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      const skipLink = this.el.closest('br-skip-link') as HTMLBrSkipLinkElement
      if (skipLink !== null && typeof skipLink.hide === 'function') {
        event.preventDefault()
        skipLink.hide()
      }
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.navigateToTarget()
      return
    }

    // atalho Alt+[keyNumber]
    if (event.altKey && this.keyNumber && event.key === this.keyNumber.toString()) {
      event.preventDefault()
      this.navigateToTarget()
      return
    }
  }

  /**
   * Listener para foco no item.
   */
  @Listen('focus')
  handleFocus() {
    this.brSkiplinkItemFocus.emit({ itemId: this.customId })
  }

  componentWillLoad() {
    this.updateItemCount()
  }

  componentDidLoad() {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'data-item-index' || mutation.attributeName === 'data-item-total')
        ) {
          this.updateItemCount()
        }
      })
    })

    this.mutationObserver.observe(this.el, {
      attributes: true,
      attributeFilter: ['data-item-index', 'data-item-total'],
    })
  }

  disconnectedCallback() {
    if (this.mutationObserver !== null && this.mutationObserver !== undefined) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }
  }

  /**
   * Atualiza a contagem de itens lendo os atributos definidos pelo componente pai.
   */
  private updateItemCount() {
    const indexAttr = this.el.getAttribute('data-item-index')
    const totalAttr = this.el.getAttribute('data-item-total')

    if (indexAttr !== null && totalAttr !== null) {
      this.itemIndex = parseInt(indexAttr, 10)
      this.itemTotal = parseInt(totalAttr, 10)
    } else {
      this.itemIndex = null
      this.itemTotal = null
    }
  }

  /**
   * Retorna a string de contagem no formato (X/Y) se disponível.
   */
  private getItemCountText(): string | null {
    if (this.itemIndex !== null && this.itemTotal !== null) {
      return ` (${this.itemIndex}/${this.itemTotal})`
    }
    return null
  }

  render() {
    const itemCountText = this.getItemCountText()

    return (
      <Host>
        <br-item
          href={this.target}
          id={this.customId}
          density="large"
          aria-keyshortcuts={this.keyNumber !== undefined ? `Alt+${this.keyNumber}` : undefined}
        >
          <slot></slot>
          {itemCountText && <span aria-hidden="true">{itemCountText}</span>}
          {this.keyNumber && !this.hideTag && (
            <slot name="tag" slot="end">
              <br-tag class="ml-1 text" color="bg-interactive">
                {this.keyNumber}
              </br-tag>
            </slot>
          )}
        </br-item>
      </Host>
    )
  }
}
